import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { updateDocument } from '../../services/docuvaultService';
import BeneficiaryCheckCard from './BeneficiaryCheckCard';

const { FiX, FiShield, FiUsers, FiCheckCircle } = FiIcons;

function parseParties(raw) {
  try {
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function DocuVaultBeneficiaryChecks({ isOpen, onClose, documents = [], onUpdated }) {
  const [search, setSearch] = useState('');
  const entries = useMemo(() => (documents ?? []).flatMap(document =>
    parseParties(document?.authorized_parties)
      .map((party, index) => ({ document, party, index }))
      .filter(entry => (entry.party?.role ?? '').toLowerCase() === 'beneficiary')
  ), [documents]);

  const filteredEntries = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return entries;
    return entries.filter(({ document, party }) =>
      (party?.name ?? '').toLowerCase().includes(query) ||
      (party?.email ?? '').toLowerCase().includes(query) ||
      (document?.original_name ?? '').toLowerCase().includes(query)
    );
  }, [entries, search]);

  const completedCount = entries.filter(({ party }) =>
    party?.beneficiary_checks?.reviewed_at &&
    ['legal_name', 'contact', 'relationship', 'scope'].every(key => party?.beneficiary_checks?.checks?.[key] === true)
  ).length;

  const saveChecks = async (entry, checks) => {
    const parties = parseParties(entry.document?.authorized_parties);
    const allComplete = ['legal_name', 'contact', 'relationship', 'scope'].every(key => checks?.[key] === true);
    const beneficiaryChecks = {
      checks,
      reviewed_at: allComplete ? new Date().toISOString() : null,
    };
    const updatedParties = parties.map((party, index) => index === entry.index
      ? { ...party, beneficiary_checks: beneficiaryChecks }
      : party
    );
    const response = await updateDocument(entry.document?.id, {
      authorized_parties: JSON.stringify(updatedParties),
    });
    if (response?.status !== 'success' && !response?.id) {
      throw new Error(response?.error || 'Could not save beneficiary checks.');
    }
    await onUpdated?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[240] flex items-center justify-center bg-accent-navy/70 p-4 backdrop-blur-md">
      <motion.section
        role="dialog"
        aria-modal="true"
        aria-labelledby="beneficiary-checks-title"
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-[32px] border border-brand-dark bg-white shadow-2xl"
      >
        <header className="flex items-center justify-between gap-4 bg-gradient-to-r from-accent-navy to-purple-900 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-accent-gold text-accent-navy">
              <SafeIcon icon={FiShield} className="h-5 w-5" />
            </div>
            <div>
              <h2 id="beneficiary-checks-title" className="font-serif text-xl text-white">Beneficiary Checks</h2>
              <p className="text-[10px] font-black uppercase tracking-widest text-white/60">
                Owner-managed review · {completedCount} of {entries.length} complete
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close beneficiary checks" className="rounded-xl p-2 text-white/70 transition hover:bg-white/10 hover:text-white">
            <SafeIcon icon={FiX} className="h-5 w-5" />
          </button>
        </header>

        <div className="border-b border-amber-200 bg-amber-50 px-6 py-3 text-xs leading-relaxed text-amber-900">
          These are manual review reminders, not identity verification, legal advice, or a background check.
          Confirm evidence through your own trusted process before granting access.
        </div>

        <div className="flex items-center gap-3 border-b border-brand-light bg-brand-light/30 p-4">
          <SafeIcon icon={FiUsers} className="h-4 w-4 text-text-muted" />
          <input
            type="search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search beneficiary or document"
            aria-label="Search beneficiaries"
            className="min-w-0 flex-1 rounded-xl border-2 border-brand-dark bg-white px-3 py-2 text-sm outline-none transition focus:border-accent-gold"
          />
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto bg-brand-light/20 p-5">
          {filteredEntries.length ? (
            <AnimatePresence>
              {filteredEntries.map(entry => (
                <BeneficiaryCheckCard
                  key={`${entry.document?.id ?? ''}-${entry.index}`}
                  entry={entry}
                  onSave={saveChecks}
                />
              ))}
            </AnimatePresence>
          ) : (
            <div className="rounded-2xl border border-dashed border-brand-dark bg-white p-10 text-center">
              <SafeIcon icon={FiCheckCircle} className="mx-auto mb-3 h-8 w-8 text-text-muted/50" />
              <h3 className="font-serif text-lg text-accent-navy">
                {entries.length ? 'No matches found' : 'No beneficiaries listed yet'}
              </h3>
              <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-text-muted">
                {entries.length
                  ? 'Try a different name, email, or document title.'
                  : 'Add a party with the Beneficiary role in Manage Access to start a review checklist.'}
              </p>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-brand-light bg-white p-4">
          <p className="text-[10px] text-text-muted">Checks are saved with the beneficiary access details for each document.</p>
          <button type="button" onClick={onClose} className="rounded-xl bg-accent-navy px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-white transition hover:bg-accent-navy/90">
            Done
          </button>
        </footer>
      </motion.section>
    </div>
  );
}